import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User } from '../types/interfaces';
import { Role } from '../types/enums';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Get current user from localStorage (simplified)
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = [Role.SuperAdmin, Role.Admin].includes(currentUser.role);
  const isSuperAdmin = currentUser.role === Role.SuperAdmin;
  const [resetEmail, setResetEmail] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/users`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setUsers(data);
    } catch (err: any) {
      console.error('Fetch users error:', err);
      setError('Failed to fetch users: ' + err.message);
    }
  };

  const handleAddUser = () => {
    if (!isAdmin) {
      setError('Only Super Admin or Admin can add users');
      return;
    }
    navigate('/users/new');
  };

  const handleDeleteUsers = async () => {
    if (!isAdmin) {
      setError('Only Super Admin or Admin can delete users');
      return;
    }
    if (selectedUsers.length === 0) {
      setError('No users selected to delete');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/users`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: selectedUsers }),
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      setSelectedUsers([]);
      fetchUsers();
      setError(null);
    } catch (err: any) {
      console.error('Delete users error:', err);
      setError('Failed to delete users: ' + err.message);
    }
  };

  const handleToggleEnable = async (enable: boolean) => {
    if (!isAdmin) {
      setError('Only Super Admin or Admin can enable/disable users');
      return;
    }
    if (selectedUsers.length === 0) {
      setError('No users selected to enable/disable');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/users`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: selectedUsers, enabled: enable }),
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      setSelectedUsers([]);
      fetchUsers();
      setError(null);
    } catch (err: any) {
      console.error('Toggle enable error:', err);
      setError(`Failed to ${enable ? 'enable' : 'disable'} users: ` + err.message);
    }
  };

  const openReset = (email: string) => {
    setResetEmail(email);
    setNewPassword('');
    setConfirmPassword('');
    setResetError(null);
    setNotice(null);
  };

  const closeReset = () => {
    setResetEmail(null);
    setNewPassword('');
    setConfirmPassword('');
    setResetError(null);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }
    setResetting(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/users/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, newPassword }),
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      setNotice(`Password reset for ${resetEmail}`);
      closeReset();
    } catch (err: any) {
      setResetError('Failed to reset password: ' + err.message);
    } finally {
      setResetting(false);
    }
  };

  const handleCheckboxChange = (email: string) => {
    setSelectedUsers((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  return (
    <div className="page-container" style={{ overflowY: 'auto', maxHeight: '100vh' }}>
      <h2 className="app-header" style={{ color: '#EEC930', fontSize: '24px', marginBottom: '20px' }}>Users List</h2>
      {error && <div className="error">{error}</div>}
      <div className="inventory-actions">
        <button onClick={handleAddUser} disabled={!isAdmin}>Add User</button>
        <button onClick={handleDeleteUsers} disabled={!isAdmin || selectedUsers.length === 0}>Delete User</button>
        <button onClick={() => handleToggleEnable(true)} disabled={!isAdmin || selectedUsers.length === 0}>Enable User</button>
        <button onClick={() => handleToggleEnable(false)} disabled={!isAdmin || selectedUsers.length === 0}>Disable User</button>
      </div>
      {notice && <div className="success-message" role="status">{notice}</div>}
      {isSuperAdmin && resetEmail && (
        <form className="user-reset-password" onSubmit={handleResetPassword} aria-label="Reset password">
          <h3>Reset password for {resetEmail}</h3>
          {resetError && <div className="error">{resetError}</div>}
          <label>
            New password
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </label>
          <label>
            Confirm password
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </label>
          <button type="submit" disabled={resetting}>Set password</button>
          <button type="button" onClick={closeReset}>Cancel</button>
        </form>
      )}
      <div className="inventory-table-container">
        <table className="inventory-table">
          <thead>
            <tr>
              <th>Select</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              {isSuperAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.email}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.email)}
                    onChange={() => handleCheckboxChange(user.email)}
                    disabled={!isAdmin}
                  />
                </td>
                <td>
                  <Link to={`/users/${encodeURIComponent(user.email)}`}>{user.email}</Link>
                </td>
                <td>{user.role}</td>
                <td>{user.enabled ? 'Enabled' : 'Disabled'}</td>
                {isSuperAdmin && (
                  <td>
                    <button type="button" onClick={() => openReset(user.email)}>
                      Reset password
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;